import { Injectable } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class AppDetectionService {

  private readonly APP_SCHEME = 'ioncourt://';
  private readonly UNIVERSAL_LINK = 'https://ioncourt.com'; // Your universal link domain
  private readonly TIMEOUT_MS = 1000; // 1 second timeout
  private lastDetectionMethod = 'unknown';

  constructor() { }

  /**
   * Detects if the native app is installed using universal links with fallback to custom scheme
   * @returns Promise<boolean> - true if app is installed, false otherwise
   */
  async isAppInstalled(): Promise<boolean> {
    // If we're already in the native app, return true
    if (Capacitor.isNativePlatform()) {
      return true;
    }

    // If we're in a web browser, try universal link first, then custom scheme
    // if (Capacitor.getPlatform() === 'web') {
    //   return this.testUniversalLinkWithFallback();
    // }

    return false;
  }

  /**
   * Tests universal link first, then falls back to custom scheme if needed
   * @returns Promise<boolean> - true if app is installed, false otherwise
   */
  private async testUniversalLinkWithFallback(): Promise<boolean> {
    try {
      // First, try universal link detection
      const universalLinkResult = await this.testUniversalLink();
      if (universalLinkResult !== null) {
        return universalLinkResult;
      }
      
      // If universal link detection is inconclusive, fall back to custom scheme
      //console.log('Universal link detection inconclusive, falling back to custom scheme');
      return this.testCustomSchemeSafariSafe();
      
    } catch (error) {
      console.error('Error in universal link detection:', error);
      // Fall back to custom scheme on error
      return this.testCustomSchemeSafariSafe();
    }
  }

  /**
   * Tests if universal link/app link can be opened (more reliable than custom schemes)
   * @returns Promise<boolean | null> - true if app installed, false if not, null if inconclusive
   */
  private async testUniversalLink(): Promise<boolean | null> {
    return new Promise((resolve) => {
      const testPath = `/app-test-${Date.now()}`;
      const universalUrl = `${this.UNIVERSAL_LINK}${testPath}`;
      let hasResolved = false;
      
      // Set a timeout to resolve as inconclusive
      const timeoutId = window.setTimeout(() => {
        if (!hasResolved) {
          hasResolved = true;
          resolve(null); // Inconclusive, will fall back to custom scheme
        }
      }, this.TIMEOUT_MS);
      
      // Enhanced platform detection for better App Links/Universal Links support
      const platform = this.detectPlatform();
      
      if (platform === 'ios') {
        // For iOS devices, use universal link testing
        this.testUniversalLinkIOS(universalUrl, timeoutId, hasResolved, resolve);
      } else if (platform === 'android') {
        // For Android devices, use app link testing
        this.testAppLinkAndroid(universalUrl, timeoutId, hasResolved, resolve);
      } else {
        // For other platforms/browsers, use generic approach
        this.testUniversalLinkOtherBrowsers(universalUrl, timeoutId, hasResolved, resolve);
      }
    });
  }

  /**
   * iOS-specific universal link testing
   */
  private testUniversalLinkIOS(
    universalUrl: string, 
    timeoutId: number, 
    hasResolved: boolean, 
    resolve: (value: boolean | null) => void
  ): void {
    try {
      // For iOS Safari, we can use a more reliable method
      // Create a hidden iframe to test the universal link
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      
      iframe.onload = () => {
        if (!hasResolved) {
          hasResolved = true;
          clearTimeout(timeoutId);
          document.body.removeChild(iframe);
          // If iframe loads, universal link didn't work (app not installed)
          resolve(false);
        }
      };
      
      iframe.onerror = () => {
        if (!hasResolved) {
          hasResolved = true;
          clearTimeout(timeoutId);
          document.body.removeChild(iframe);
          // If iframe errors, universal link might have worked
          resolve(true);
        }
      };
      
      document.body.appendChild(iframe);
      iframe.src = universalUrl;
      
    } catch (error) {
      if (!hasResolved) {
        hasResolved = true;
        clearTimeout(timeoutId);
        resolve(null); // Inconclusive
      }
    }
  }

  /**
   * Android-specific app link testing
   */
  private testAppLinkAndroid(
    universalUrl: string, 
    timeoutId: number, 
    hasResolved: boolean, 
    resolve: (value: boolean | null) => void
  ): void {
    try {
      // For Android, app links work well with window.open
      // Android Chrome and other browsers handle app links properly
      const newWindow = window.open(universalUrl, '_blank', 'width=1,height=1,scrollbars=no,resizable=no');
      
      if (newWindow === null) {
        // If window.open returns null, app link might have worked
        if (!hasResolved) {
          hasResolved = true;
          clearTimeout(timeoutId);
          resolve(true);
        }
      } else {
        // If a new window opened, close it and assume app link didn't work
        if (newWindow) {
          newWindow.close();
        }
        if (!hasResolved) {
          hasResolved = true;
          clearTimeout(timeoutId);
          resolve(false);
        }
      }
    } catch (error) {
      if (!hasResolved) {
        hasResolved = true;
        clearTimeout(timeoutId);
        resolve(null); // Inconclusive
      }
    }
  }

  /**
   * Universal link testing for non-iOS browsers
   */
  private testUniversalLinkOtherBrowsers(
    universalUrl: string, 
    timeoutId: number, 
    hasResolved: boolean, 
    resolve: (value: boolean | null) => void
  ): void {
    try {
      // For other browsers, use window.open with universal link
      const newWindow = window.open(universalUrl, '_blank', 'width=1,height=1,scrollbars=no,resizable=no');
      
      if (newWindow === null) {
        // If window.open returns null, universal link might have worked
        if (!hasResolved) {
          hasResolved = true;
          clearTimeout(timeoutId);
          resolve(true);
        }
      } else {
        // If a new window opened, close it and assume universal link didn't work
        if (newWindow) {
          newWindow.close();
        }
        if (!hasResolved) {
          hasResolved = true;
          clearTimeout(timeoutId);
          resolve(false);
        }
      }
    } catch (error) {
      if (!hasResolved) {
        hasResolved = true;
        clearTimeout(timeoutId);
        resolve(null); // Inconclusive
      }
    }
  }

  /**
   * Platform-aware method to test if the custom URL scheme can be opened
   * This method uses a conservative approach that doesn't open new tabs
   * @returns Promise<boolean> - true if app is installed, false otherwise
   */
  private async testCustomSchemeSafariSafe(): Promise<boolean> {
    // Use enhanced platform detection
    const platform = this.detectPlatform();
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    // For iOS Safari, we'll disable app detection to avoid issues
    // Safari's security model makes reliable app detection very difficult
    if (platform === 'ios' && isSafari) {
      //console.log('App detection disabled for iOS Safari to avoid tab opening issues');
      return false;
    }
    
    // For other browsers, we can try a more reliable method
    return this.testCustomSchemeOtherBrowsers();
  }

  /**
   * App detection method for non-Safari browsers
   * @returns Promise<boolean> - true if app is installed, false otherwise
   */
  private async testCustomSchemeOtherBrowsers(): Promise<boolean> {
    return new Promise((resolve) => {
      const testUrl = `${this.APP_SCHEME}test-${Date.now()}`;
      let hasResolved = false;
      
      // Set a timeout to resolve as false
      const timeoutId = window.setTimeout(() => {
        if (!hasResolved) {
          hasResolved = true;
          resolve(false);
        }
      }, this.TIMEOUT_MS);
      
      // For non-Safari browsers, we can try a more reliable method
      // Use window.open but handle it more carefully
      try {
        const newWindow = window.open(testUrl, '_blank', 'width=1,height=1,scrollbars=no,resizable=no');
        
        if (newWindow === null) {
          // If window.open returns null, it might mean the app opened
          if (!hasResolved) {
            hasResolved = true;
            clearTimeout(timeoutId);
            resolve(true);
          }
        } else {
          // If a new window opened, close it and assume app is not installed
          if (newWindow) {
            newWindow.close();
          }
          if (!hasResolved) {
            hasResolved = true;
            clearTimeout(timeoutId);
            resolve(false);
          }
        }
      } catch (error) {
        if (!hasResolved) {
          hasResolved = true;
          clearTimeout(timeoutId);
          resolve(false);
        }
      }
    });
  }

  /**
   * Enhanced platform detection for App Links/Universal Links
   * @returns string - platform name (ios, android, web)
   */
  private detectPlatform(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // iOS detection
    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'ios';
    }
    
    // Android detection
    if (/android/.test(userAgent)) {
      return 'android';
    }
    
    // Default to web
    return 'web';
  }

  /**
   * Get the platform information
   * @returns Promise<string> - platform name (ios, android, web)
   */
  async getPlatform(): Promise<string> {
    if (Capacitor.isNativePlatform()) {
      const deviceInfo = await Device.getInfo();
      return deviceInfo.platform;
    }
    return 'web';
  }

  /**
   * Get detailed app installation information
   * @returns Promise<object> - object containing platform and installation status
   */
  async getAppInstallationInfo(): Promise<{
    platform: string;
    isInstalled: boolean;
    isNative: boolean;
    detectionMethod: string;
    userAgent: string;
  }> {
    const platform = await this.getPlatform();
    const isInstalled = await this.isAppInstalled();
    const isNative = Capacitor.isNativePlatform();
    
    // Determine detection method used
    const detectedPlatform = this.detectPlatform();
    let detectionMethod = 'unknown';
    
    if (isNative) {
      detectionMethod = 'native_platform';
    } else if (detectedPlatform === 'ios') {
      // For iOS devices, check if universal link worked or fell back to custom scheme
      detectionMethod = 'universal_link_ios';
    } else if (detectedPlatform === 'android') {
      // For Android devices, check if app link worked or fell back to custom scheme
      detectionMethod = 'app_link_android';
    } else if (Capacitor.getPlatform() === 'web') {
      // For other browsers, check if universal link/app link worked or fell back to custom scheme
      detectionMethod = 'universal_link_other';
    }

    return {
      platform,
      isInstalled,
      isNative,
      detectionMethod,
      userAgent: navigator.userAgent
    };
  }
}
